import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LabelComponent } from "@components/label/label.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { ToggleListItemComponent } from "@components/toggle-list/toggle-list-item/toggle-list-item.component";
import { ToggleListComponent } from "@components/toggle-list/toggle-list.component";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { KundorderTyp } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { KundorderradEditComponent } from "../../kundorder/kundorder-details/kundorderrad-edit/kundorderrad-edit.component";
import { IKundorderDetailKundorder, IKundorderDetailKundorderrad } from "../../kundorder/models/kundorder-detail-model";
import { CopyKundorderCommand } from "./commands/CopyKundorderCommand";
import { CopyOrderHeaderComponent } from "./copy-order-header/copy-order-header.component";
import { CopyOrderHeaderValidation } from "./copy-order-header/copy-order-header.validation";
import { ICopyOrderHeader } from "./copy-order.models";
import { CopyOrderService } from "./copy-order.service";
import { CopyKundorderCompletedEventMessage } from "./events/CopyKundorderCompletedEventMessage";

@Component({
    selector: "rask-copy-order",
    templateUrl: "./copy-order.component.html",
    styleUrls: ["./copy-order.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        CopyOrderHeaderComponent,
        ToggleListComponent,
        LabelComponent,
        ToggleListItemComponent,
        KundorderradEditComponent,
        SaveButtonComponent
    ]
})
export class CopyOrderComponent implements OnInit, OnDestroy {
    header: ICopyOrderHeader;
    kundorder: IKundorderDetailKundorder;
    kundorderTyp: string;
    rootForm: UntypedFormGroup;
    validationContext: IValidationContext;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly copyOrderService = inject(CopyOrderService);
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(RaskModalService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly router = inject(Router);

    private copyWorkComplete$: Subject<void>;

    get pageTitle(): string {
        return this.kundorderTyp === KundorderTyp.Kundorder ? this.kundorder?.kund?.namn : "Terminalorder";
    }

    canEdit(): boolean {
        return this.authService.canEdit();
    }

    canSave(): boolean {
        return this.rootForm.dirty &&
            !this.validationContext.hasErrors(ValidationScopes.skapa) &&
            this.kundorder.kundorderrader.some(k => k.kopiera);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        const snapshot = this.activatedRoute.snapshot;
        this.kundorderTyp = snapshot.data.typ;
        const id = +snapshot.paramMap.get("id");

        this.busyService.addWork(this.copyOrderService
            .getPreview(id))
            .subscribe(res => {
                this.header = res.header;
                this.kundorder = res.order;
                this.validationContext = CopyOrderHeaderValidation.init(this.header);

                this.eventHubService.registerListener(`kundorder/${this.kundorder.id.toString()}`);
            });

        this.rootForm = new UntypedFormGroup({});
        this.setupFormListener();

        this.eventService
            .subscribeEvent<CopyKundorderCompletedEventMessage>(CopyKundorderCompletedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.kundorderId === this.kundorder.id))
            .subscribe(event => {
                // eslint-disable-next-line sonarjs/deprecation
                if (this.copyWorkComplete$ && !this.copyWorkComplete$.isStopped) {
                    this.copyWorkComplete$.next();
                    this.copyWorkComplete$.complete();
                }

                if (ObjectUtils.isNullOrUndefined(event.validationMessage)) {
                    if (this.kundorderTyp === KundorderTyp.Kundorder) {
                        this.router.navigate([
                            "kundorder",
                            event.targetKundorderId
                        ]);
                    } else {
                        this.router.navigate([
                            "terminalorder",
                            event.targetKundorderId
                        ]);
                    }
                } else {
                    this.showValidationMessage(event.validationMessage);
                }
            });
    }

    save = (): void => {
        try {
            this.copyWorkComplete$ = new Subject();
            this.busyService.addWork(this.copyWorkComplete$).subscribe();

            // Reset commands from kundorderrad-edit
            this.commandService.reset();

            const command = new CopyKundorderCommand((x) => {
                x.kundorderId = this.kundorder.id;
                x.periodFrom = this.header.periodFrom;
                x.periodTom = this.header.periodTom;
                x.kundordertyp = this.kundorderTyp;
                x.kundorderrader = this.kundorder.kundorderrader.filter(k => k.kopiera).map(k => k.id);
            });

            // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
            const local = this.commandService.createInstance();
            local.addTransactionCommand(command);
            local.pushCommandQueueTransaction().subscribe({
                error: () => {
                    this.copyWorkComplete$.next();
                    this.copyWorkComplete$.complete();
                }
            });
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (e) {
            this.copyWorkComplete$.next();
            this.copyWorkComplete$.complete();
        }
    };

    private setSubFormValue(kor: IKundorderDetailKundorderrad, controlName: string): void {
        const subForm = this.rootForm.get(`kundorder${kor.id}`);
        if (subForm) {
            subForm.get(controlName).setValue(kor[controlName] || null);
        }
    }

    private setupFormListener(): void {
        const subFormName = "copy-order-header";
        if (!this.rootForm.contains(subFormName)) {
            setTimeout(() => {
                this.setupFormListener();
            }, 500);
            return;
        }

        this.rootForm.get(subFormName).get("periodFrom").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((newValue) => {
                this.kundorder.kundorderrader.forEach(x => {
                    x.periodFrom = newValue;
                    this.setSubFormValue(x, "periodFrom");
                });
            });

        this.rootForm.get(subFormName).get("periodTom").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((newValue) => {
                this.kundorder.kundorderrader.forEach(x => {
                    x.periodTom = newValue;
                    this.setSubFormValue(x, "periodTom");
                });
            });
    }

    private showValidationMessage(message: string): void {
        const msg = {
            canForce: false,
            isHandled: false,
            validationMessages: [
                { severity: 2,
                    message: message }
            ]
        };
        this.modalService.error("Fel", msg as any).subscribe();
    }
}
